/**
 * Тип компании + окно клиента — один запрос на вкладку, а не на компонент.
 *
 * Профиль спрашивают и меню, и полоса «вы в окне клиента», и карточка клиента.
 * Без общего кэша это три одинаковых запроса на каждой навигации — ровно тот
 * класс мелких задержек, из которых складывается усталость от системы.
 *
 * Живёт модульным промисом, а не react-query: react-query в этом проекте на
 * AppShell-поверхностях уже давал конфликты в тестах, а здесь нужен ровно
 * один разделяемый запрос.
 */
import { useEffect, useState } from 'react';
import { getConsultantProfile } from './api';
import type { ConsultantProfile } from './types';

let cached: Promise<ConsultantProfile | null> | null = null;

/** Сбросить кэш — после входа/выхода из окна клиента и в тестах. */
export function invalidateConsultantProfile() {
  cached = null;
}

function load(): Promise<ConsultantProfile | null> {
  if (!cached) {
    cached = getConsultantProfile().catch(() => null);
  }
  return cached;
}

export interface ConsultantProfileState {
  profile: ConsultantProfile | null;
  loading: boolean;
  /** Показывать ли раздел «Клиенты». */
  isConsultant: boolean;
  /** Есть ли у компании свои поля (у чистого консультанта — нет). */
  hasOwnFields: boolean;
}

export function useConsultantProfile(): ConsultantProfileState {
  const [profile, setProfile] = useState<ConsultantProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void load().then((p) => {
      if (cancelled) return;
      setProfile(p);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const kind = profile?.business_kind ?? 'agro';
  const consultantKind = kind === 'consultant' || kind === 'hybrid';
  // Тип компании — необходимое условие, подключённый источник — достаточное.
  // Показать «Клиенты» консультанту без подключённого списка значит завести
  // его в 404: сервер отвечает именно так — раздела для него нет.
  const isConsultant = consultantKind && profile?.clients_source_connected === true;

  return {
    profile,
    loading,
    isConsultant,
    // Гибрид сеет и сам — «Поля» у него остаются. Прячем раздел только у
    // чистого консультанта: своих полей у него не бывает по определению.
    //
    // `|| !isConsultant` — страховка от пустого меню: если компания помечена
    // консультантом, а список клиентов ей не подключён (недонастроили), она
    // осталась бы и без «Клиентов», и без «Полей», то есть без единого входа
    // в работу. Лучше показать раздел, который окажется пустым, чем оставить
    // человека перед меню без пунктов.
    hasOwnFields: kind !== 'consultant' || !isConsultant,
  };
}
